import './ServiceOrderTechnicianStock.scss';

import { DeleteOutlined, EditOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Empty, Modal, Tag } from 'antd';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import {
  SERVICE_ORDER_ITEM_RESOLUTION,
  ServiceOrderItem,
} from '@/model/ServiceOrder/ServiceOrderItem';

import ServiceOrderTechnicianItemForm from './ServiceOrderTechnicianItemForm';

interface ServiceOrderTechnicianStockProps {
  serviceOrderItems: ServiceOrderItem[];
  // Permite updates funcionales para evitar sobreescrituras.
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
}

interface StockItemSummaryRowProps {
  item: ServiceOrderItem;
  onEdit: () => void;
  onRemove: () => void;
  onRestore: () => void;
}

const StockItemSummaryRow = (props: StockItemSummaryRowProps) => {
  const { t } = useTranslation();
  const { item, onEdit, onRemove, onRestore } = props;
  const productName = item.name ?? '-';
  const serialLabel = item.serial_number ?? t('No indicado');
  const quantityLabel = item.used_amount ?? '-';

  return (
    <div className="ServiceOrderTechnicianStockItem">
      <div className="ServiceOrderTechnicianStockItemInfo">
        <div className="ServiceOrderTechnicianStockItemTitle">
          {item.toDelete ? (
            <del className="ServiceOrderTechnicianStockItemDeleted">{productName}</del>
          ) : (
            productName
          )}{' '}
          {(item.in_warranty || item.resolution_status) && (
            <>
              {item.in_warranty && <Tag color="green">{t('En garantía')}</Tag>}
              {item.resolution_status === SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_TO_BILL && (
                <Tag color="blue">{t('A Facturar')}</Tag>
              )}
              {item.resolution_status === SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_PERITAGE && (
                <Tag color="orange">{t('Sujeto a peritaje')}</Tag>
              )}
              {item.resolution_status ===
                SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_BONUS_APPLIED && (
                <Tag color="cyan">{t('Bonificado')}</Tag>
              )}
              {item.resolution_status === SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_AUTOMATIC && (
                <Tag color="purple">{t('Resolución Automática')}</Tag>
              )}
            </>
          )}
        </div>
        <div className="ServiceOrderTechnicianStockItemMeta">
          {item.product_type != PRODUCT_TYPES.PRODUCT && (
            <div>{`${t('Número de serie')}: ${serialLabel}`}</div>
          )}
          <div>{`${t('Cantidad')}: ${quantityLabel}`}</div>
        </div>
      </div>
      <div className="ServiceOrderTechnicianStockItemActions">
        <Button size="small" icon={<EditOutlined />} onClick={onEdit} aria-label={t('Editar')} />
        {item.toDelete ? (
          <Button
            size="small"
            icon={<UndoOutlined />}
            onClick={onRestore}
            aria-label={t('Restaurar')}
          />
        ) : (
          <Button
            size="small"
            icon={<DeleteOutlined />}
            onClick={onRemove}
            danger
            aria-label={t('Borrar')}
          />
        )}
      </div>
    </div>
  );
};

const ServiceOrderTechnicianStock = (props: ServiceOrderTechnicianStockProps) => {
  const { t } = useTranslation();
  const { serviceOrderItems, setServiceOrderItems } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ServiceOrderItem>();
  const openModal = (item?: ServiceOrderItem) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(undefined);
  };

  const handleSaveItem = (values: Partial<ServiceOrderItem>) => {
    if (!editingItem) {
      setServiceOrderItems((prevState: ServiceOrderItem[]) => [
        ...prevState,
        {
          ...values,
        } as ServiceOrderItem,
      ]);
      closeModal();

      return;
    }

    setServiceOrderItems((prevState: ServiceOrderItem[]) => {
      const nextItem: ServiceOrderItem = {
        ...editingItem,
        ...values,
        toUpdate: editingItem.id ? true : editingItem.toUpdate,
      };
      const existingIndex = editingItem.id
        ? prevState.findIndex(item => item.id === editingItem.id)
        : prevState.indexOf(editingItem);

      if (existingIndex === -1) {
        return [...prevState, nextItem];
      }

      const nextItems = [...prevState];

      nextItems[existingIndex] = nextItem;

      return nextItems;
    });
    closeModal();
  };

  const handleStockItemRemove = (id: number) => {
    if (!id) {
      // Si no hay ID, se elimina directamente.
      setServiceOrderItems((prevState: ServiceOrderItem[]) =>
        prevState.filter(item => item.id !== id),
      );

      return;
    }

    const itemToRemove = serviceOrderItems.find(item => item.id === id);

    if (!itemToRemove) {
      return;
    }

    if (itemToRemove.id) {
      // Si el item tiene ID, se marca para eliminación.
      setServiceOrderItems((prevState: ServiceOrderItem[]) =>
        prevState.map(item => (item.id === id ? { ...item, toDelete: true } : item)),
      );

      return;
    }
  };

  const handleStockItemRestore = (id: number) => {
    const itemToRemove = serviceOrderItems.find(item => item.id === id);

    if (!itemToRemove) {
      return;
    }

    if (itemToRemove.id) {
      // Si el item tiene ID, se marca para restauración.
      setServiceOrderItems((prevState: ServiceOrderItem[]) =>
        prevState.map(item => (item.id === id ? { ...item, toDelete: false } : item)),
      );

      return;
    }
  };

  return (
    <div className="ServiceOrderTechnicianStock">
      <div className="ServiceOrderTechnicianStockTitle">{t('Stock utilizado del técnico')}</div>
      {serviceOrderItems.length === 0 && (
        <Empty description={t('Agrega items si fueron utilizados del stock del técnico')} />
      )}
      <div className="ServiceOrderTechnicianStockList">
        {serviceOrderItems.map((item, index) => (
          <StockItemSummaryRow
            key={item.name + index}
            item={item}
            onEdit={() => openModal(item)}
            onRemove={() => handleStockItemRemove(item.id)}
            onRestore={() => handleStockItemRestore(item.id)}
          />
        ))}
      </div>
      <Button icon={<PlusOutlined />} onClick={() => openModal()}>
        {t('Agregar item')}
      </Button>
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        width={700}
        footer={null}
        maskClosable={false}
        destroyOnHidden={true}
        title={
          editingItem
            ? t('Editar item de stock del técnico')
            : t('Agregar item de stock del técnico')
        }>
        <ServiceOrderTechnicianItemForm
          item={editingItem}
          onSave={handleSaveItem}
          onCancel={closeModal}
        />
      </Modal>
    </div>
  );
};

export default ServiceOrderTechnicianStock;
